import apis from './apis';

export interface ParentChainItem {
  id: string;
  name: string;
}

export interface CurrentFolder {
  id: string;
  name: string;
  FolderID?: string;
  FolderName?: string;
  ParentChain?: ParentChainItem[];
}

export interface FileApiResponse {
  items?: any[];
  files: any[];
  folders?: any[];
  subfolders?: any[];
  collaborators?: any[];
  currentFolder?: CurrentFolder | null;
  hierarchy?: Record<string, string>;
  pagination: {
    page: number;
    limit: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
    shown_items: number;
  };
}

/**
 * Fetch root files and folders
 * GET /files/get_file_structure
 */
export async function fetchRootFiles(options?: {
  page?: number;
  limit?: number;
  searchTerm?: string;
  sortBy?: string;
  sortOrder?: string;
}): Promise<FileApiResponse> {
  try {
    const {
      page = 1,
      limit = 50,
      searchTerm = '',
      sortBy = 'created_at',
      sortOrder = 'desc'
    } = options || {};

    const params = new URLSearchParams({
      sortBy,
      sortOrder,
      page: page.toString(),
      limit: limit.toString()
    });

    if (searchTerm) {
      params.append('searchTerm', searchTerm);
    }

    const response = await apis.get<FileApiResponse>(`/files/get_file_structure?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching root files:', error);
    throw error;
  }
}

/**
 * Fetch folder contents and subfolders
 * GET /files/get_file_and_sub_folder?folderID={id}&parentFolderID={parentId}
 */
export async function fetchFolderContents(
  folderId: string,
  page: number = 1,
  parentFolderId?: string,
  options?: {
    limit?: number;
    searchTerm?: string;
    sortBy?: string;
    sortOrder?: string;
  }
): Promise<FileApiResponse> {
  try {
    const {
      limit = 50,
      searchTerm = '',
      sortBy = 'created_at',
      sortOrder = 'desc'
    } = options || {};

    const params = new URLSearchParams({
      folderID: folderId,
      sortBy,
      sortOrder,
      page: page.toString(),
      limit: limit.toString()
    });

    if (searchTerm) {
      params.append('searchTerm', searchTerm);
    }

    // Pass parent folder ID for nested folder access context
    if (parentFolderId) {
      params.append('parentFolderID', parentFolderId);
    }

    const response = await apis.get<FileApiResponse>(`/files/get_file_and_sub_folder?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching folder ${folderId}:`, error);
    throw error;
  }
}

/**
 * Get file content
 * POST /files/get-file
 */
export async function getFile(fileId: string, folderId: string, parentFolderId?: string): Promise<any> {
  try {
    const body: any = {
      FileID: fileId,
      FolderID: folderId
    };

    // Pass parent folder ID for nested folder support
    if (parentFolderId) {
      body.ParentFolderID = parentFolderId;
    }

    const response = await apis.post<any>(`/files/get-file`, body);
    return response.data;
  } catch (error) {
    console.error(`Error getting file ${fileId}:`, error);
    throw error;
  }
}

/**
 * Stream file content
 * POST /files/stream-file
 */
export async function streamFile(fileId: string, folderId: string, parentFolderId?: string, disposition: string = 'inline'): Promise<Blob> {
  try {
    const body: any = {
      FileID: fileId,
      FolderID: folderId,
      Disposition: disposition
    };

    // Pass parent folder ID for nested folder support
    if (parentFolderId) {
      body.ParentFolderID = parentFolderId;
    }

    const response = await apis.post(`/files/stream-file?disposition=${disposition}`, body, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error(`Error streaming file ${fileId}:`, error);
    throw error;
  }
}

/**
 * Manage access (add/remove collaborators)
 * POST /files/manage-access
 */
export interface ManageAccessRequest {
  item_id: string;
  item_type: 'file' | 'folder';
  collaborators: Array<{
    user_id: string;
    access: 'edit' | 'view' | 'admin';
  }>;
}

export async function manageAccess(data: ManageAccessRequest): Promise<any> {
  try {
    const response = await apis({
      method: 'POST',
      url: '/files/manage-access',
      data: data
    });
    return response.data;
  } catch (error) {
    console.error('Error managing access:', error);
    throw error;
  }
}
